import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Edit, Trash2, ChevronDown, ChevronUp, Search, CheckCircle, HelpCircle, XCircle, Circle, X, AlertCircle, Users } from 'lucide-react';
import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import { format, differenceInHours, differenceInDays, differenceInWeeks, startOfDay, isAfter } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import CountdownBanner from '../components/CountdownBanner';
import EventGroups from '../components/EventGroups';
import { formatAsEasternTime, formatAsLocalTime } from '../utils/timezone';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter] = useState('active');
  const [siteSettings, setSiteSettings] = useState({});
  const [userPreferences, setUserPreferences] = useState({ showCountdown: false });
  const { user } = useAuth();

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`/api/events?status=${statusFilter}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await axios.get('/api/events/site-settings');
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  }, []);

  const fetchUserPreferences = useCallback(async () => {
    try {
      const response = await axios.get('/api/users/preferences');
      setUserPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchSiteSettings();
    fetchUserPreferences();
  }, [fetchEvents, fetchSiteSettings, fetchUserPreferences]);



  // Sort events by date
  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );

  // Get all upcoming events (events that haven't happened yet)
  const today = startOfDay(new Date());
  
  // Filter out past events first
  const futureEvents = sortedEvents.filter(event => {
    const eventDate = new Date(event.date);
    return isAfter(eventDate, today);
  });

  // Get next game (closest upcoming game)
  const nextGame = futureEvents.length > 0 ? futureEvents[0] : null;

  const getStatusBadgeClass = (status, eventDate) => {
    // Check if event is happening now
    if (eventDate) {
      const now = new Date();
      const eventTime = new Date(eventDate);
      const diffInHours = differenceInHours(eventTime, now);
      
      // If event is happening now (within 30 minutes of start time or has started)
      if (diffInHours >= -0.5 && diffInHours <= 4) {
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      }
    }
    
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle size={14} />;
      case 'tentative': return <HelpCircle size={14} />;
      case 'late': return <AlertCircle size={14} />;
      case 'declined': return <XCircle size={14} />;
      default: return <HelpCircle size={14} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'late': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
      case 'tentative': return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-300';
      case 'declined': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const formatCountdown = (eventDate) => {
    const now = new Date();
    const eventTime = new Date(eventDate);
    const diffInHours = differenceInHours(eventTime, now);
    const diffInDays = differenceInDays(eventTime, now);
    const diffInWeeks = differenceInWeeks(eventTime, now);

    // Check if event has already passed
    if (diffInHours < 0) {
      return 'Event passed';
    }

    // Check if event is happening now (within 30 minutes of start time or has started)
    if (diffInHours >= -0.5 && diffInHours <= 4) {
      return 'Happening Now';
    }

    if (diffInWeeks > 0) {
      return `In ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''}`;
    } else if (diffInDays > 0) {
      return `In ${diffInDays} day${diffInDays > 1 ? 's' : ''}`;
    } else if (diffInHours > 0) {
      return `In ${diffInHours} hour${diffInHours > 1 ? 's' : ''}`;
    } else {
      return 'Starting soon';
    }
  };

  const handleQuickSignup = async (eventId, status) => {
    try {
      await axios.post(`/api/events/${eventId}/signup`, {
        status,
        notes: ''
      });
      fetchEvents(); // Refresh events to update signup counts
      
      // Show success feedback
      const statusLabels = {
        confirmed: 'Confirmed',
        late: 'Late',
        tentative: 'Tentative',
        declined: 'Declined'
      };
      
      // You could add a toast notification here

    } catch (error) {
      console.error('Failed to sign up:', error);
      alert('Failed to sign up. Please try again.');
    }
  };

  // Remove the old complex functions - we now use the simplified utility functions
  // const formatEventTime = (dateString) => { ... }
  // const formatLocalTime = (dateString) => { ... }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Events</h1>
          <p className="text-gray-600 dark:text-gray-400">Browse and sign up for Blood on the Clocktower events</p>
        </div>
        {user && (user.role === 'creator' || user.role === 'admin') && (
          <Link
            to="/create-event"
            className="btn btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
          >
            <Calendar size={16} />
            <span>Create Event</span>
          </Link>
        )}
      </div>

      {/* Configurable Banner */}
      {siteSettings.events_banner && (
        <div className="card bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-blue-900 dark:text-blue-100 font-medium">
              {siteSettings.events_banner}
            </p>
          </div>
        </div>
      )}

      {/* Countdown Banner */}
      {userPreferences.showCountdown && nextGame && (
        <CountdownBanner nextGame={nextGame} />
      )}

      {/* Upcoming Games Section */}
      {futureEvents.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Upcoming Games
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {futureEvents.map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                getStatusBadgeClass={getStatusBadgeClass}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                formatCountdown={formatCountdown}
                handleQuickSignup={handleQuickSignup}
                user={user}
                isUpcoming={true}
                formatEventTime={formatAsEasternTime}
                formatLocalTime={formatAsLocalTime}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Events Found */}
      {futureEvents.length === 0 && (
        <div className="card text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No upcoming events found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No upcoming events are currently available.
          </p>
        </div>
      )}
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, getStatusBadgeClass, getStatusIcon, getStatusColor, formatCountdown, handleQuickSignup, user, isUpcoming, formatEventTime, formatLocalTime, getTimezoneDisplayName }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleQuickSignupClick = async (status) => {
    setSignupLoading(true);
    try {
      await handleQuickSignup(event.id, status);
      setShowDropdown(false);
    } finally {
      setSignupLoading(false);
    }
  };

  const getStatusButtonClass = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700';
      case 'late': return 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900 dark:hover:bg-yellow-800 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
      case 'tentative': return 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900 dark:hover:bg-orange-800 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700';
      case 'declined': return 'bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700';
      default: return 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700';
    }
  };

  const statusOptions = [
    { value: 'confirmed', label: 'Confirm', icon: <CheckCircle size={14} /> },
    { value: 'late', label: 'Late', icon: <AlertCircle size={14} /> },
    { value: 'tentative', label: 'Maybe', icon: <HelpCircle size={14} /> },
    { value: 'declined', label: 'Decline', icon: <XCircle size={14} /> }
  ];

  return (
    <div className={`card hover:shadow-md transition-shadow ${isUpcoming ? 'border-l-4 border-l-green-500' : ''} flex flex-col`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 mr-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
            <Link to={`/events/${event.id}`} className="hover:text-primary-600">
              {event.title}
            </Link>
          </h3>
        </div>
        <div className="flex items-center space-x-1">
          {user && event.status === 'active' && (
            <div className="relative dropdown-container">
              {event.user_signup_status ? (
                // User has a signup - show current status with dropdown
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  disabled={signupLoading}
                  className={`p-1.5 rounded-md border transition-colors flex items-center space-x-1 ${getStatusButtonClass(event.user_signup_status)}`}
                  title={`Current: ${event.user_signup_status}`}
                >
                  {getStatusIcon(event.user_signup_status)}
                  <ChevronDown size={12} />
                </button>
              ) : (
                // User has no signup - show signup button
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  disabled={signupLoading}
                  className="p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700 transition-colors"
                  title="Sign up for this event"
                >
                  <Calendar size={16} />
                </button>
              )}
              
              {/* Dropdown menu */}
              {showDropdown && (
                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[120px]">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleQuickSignupClick(option.value)}
                      disabled={signupLoading}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${
                        event.user_signup_status === option.value ? 'bg-gray-50 dark:bg-gray-700' : ''
                      }`}
                    >
                      <span className={getStatusColor(option.value).split(' ')[0]}>{option.icon}</span>
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className={`badge ${getStatusBadgeClass(event.status, event.date)}`}>
            {formatCountdown(event.date)}
          </span>
        </div>
      </div>

      {event.description && (
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
          {event.description}
        </p>
      )}

      <div className="space-y-2 mb-4 flex-grow">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Clock size={14} className="mr-2" />
          <div>
            <div>
              {formatEventTime(event.date)} Eastern Time
            </div>
            {event.timezone && event.timezone !== 'UTC' && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formatLocalTime(event.date)} your local time
              </div>
            )}
          </div>
        </div>
        
        {event.location && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <MapPin size={14} className="mr-2" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
        
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Users size={14} className="mr-2" />
          <span>
            {event.confirmed_count || 0} confirmed
            {event.tentative_count > 0 && `, ${event.tentative_count} tentative`}
            {event.late_count > 0 && `, ${event.late_count} late`}
          </span>
        </div>
      </div>

      <div className="mt-auto">
        <div className="text-sm text-gray-500 mb-3">
          by {event.creator_name || 'Unknown Creator'}
        </div>
        
        <div className="flex justify-end">
          <Link
            to={`/events/${event.id}`}
            className="btn btn-primary text-sm whitespace-nowrap"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Events; 